/**
 * Bajas y consentimientos (`/activa/oficial/bajas`).
 *
 * - La lista de no contactar es UNA por marca, compartida con Activa: se embebe la misma página
 *   (`NoContactarPage embebida`) en vez de copiarla, así no hay dos listas que se desincronicen.
 * - «Buscar la constancia de un contacto» responde un reclamo (art. 5 de la Ley 25.326): quién dio
 *   el consentimiento, cuándo y de dónde, sus bajas y los mensajes oficiales que recibió.
 * - «Consentimientos» dice, por cada base analizada, a cuántos se les puede escribir por el oficial
 *   (sólo a los que dijeron que sí), contando sólo a los que tienen celular. Contaba todos los contactos: en la
 *   recorrida de QA el mismo análisis decía «sin dato» 55 acá, «nunca se les preguntó» 46 en el reporte de
 *   Reactivación (los que tienen mail o celular) y 45 en Resultados (los de ese envío), y 15 de esos 55 no tenían
 *   celular: por WhatsApp no se les puede escribir aunque digan que sí. Ahora la tabla dice sobre quiénes cuenta
 *   («Con celular», de cuántos clientes).
 *
 * Un mensaje o una baja que vino del modo simulado lleva «simulado: no salió a WhatsApp»: la
 * constancia contesta un reclamo, y no puede decir que a alguien se le mandó algo que nunca salió.
 *
 * El teléfono de la constancia se muestra SIEMPRE enmascarado (D-C: sólo los últimos 4 dígitos).
 * El backend ya lo manda enmascarado; `destinoParaMostrar` lo vuelve a enmascarar igual, por si algún
 * día llegara completo: una pantalla que se comparte en una llamada no puede depender de eso.
 */

import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Search, ShieldCheck } from "lucide-react";
import type { Consentimiento, ConstanciaContacto, FuenteOptIn } from "../types";
import { oficialApi } from "../api";
import { useCuentaOficial } from "../contexto";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { CATEGORIA, FUENTE_OPTIN, TEXTO_CONSTANCIA_RECORTADA, TEXTO_CONSTANCIA_VACIA, TOPE_CONSTANCIA } from "../logic/textos";
import { destinoParaMostrar, etiquetaAnalisis, nombreDeAutorPara, nombreDeQuienParaCliente } from "../logic/formato";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { ChipEstadoMensaje } from "../components/ChipEstadoMensaje";
import { ChipSimuladoNoSalio } from "../components/ChipSimuladoNoSalio";
import { FUENTE_BASE } from "../components/base/comun";
import { Tarjeta } from "../components/Tarjeta";
import NoContactarPage from "../../campana-nueva/pages/NoContactarPage";
import { fecha, fechaHora, num } from "../../campana-nueva/utils/format";

const R = "/activa/oficial";
/** El `campanaId` de los mensajes de PRUEBA (`ID_PRUEBA` de motor.service.ts): no es una campaña, no se linkea. */
const ID_PRUEBA = "__prueba__";

const TEXTO_OPTIN = (v: boolean | null) => (v === true ? "Dijo que sí" : v === false ? "Dijo que no" : "Sin dato");
const CLS_OPTIN = (v: boolean | null) => (v === true ? "bg-emerald-50 text-emerald-700" : v === false ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-600");

export default function BajasConsentimientosPage() {
  const { config } = useCuentaOficial();
  const boton = config?.botonBaja ? `«${config.botonBaja}»` : "el botón de baja";

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900 tracking-tight">Bajas y consentimientos</h2>
      <div className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <ShieldCheck size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700">
          Es una sola lista para toda la marca: una baja por cualquier canal (WhatsApp oficial, WhatsApp por QR o email) frena todos los envíos.
          Cuando alguien responde BAJA o toca {boton}, entra solo a la lista en menos de un minuto.
        </p>
      </div>

      <NoContactarPage embebida />

      <BuscarConstancia />

      <TablaConsentimientos />
    </div>
  );
}

function BuscarConstancia() {
  // Quién mandó lo de Conversaciones: a una persona de un cliente, alguien del equipo es «Equipo Deenex» (`nombreDeAutorPara`).
  const equipo = useEsEquipoDeenex();
  const [q, setQ] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState("");
  const [c, setC] = useState<ConstanciaContacto | null>(null);

  const buscar = async (e: FormEvent) => {
    e.preventDefault();
    const telefono = q.trim();
    if (!telefono || buscando) return;
    setBuscando(true);
    setError("");
    setC(null);
    try {
      const l = leerRespuesta<ConstanciaContacto>(await oficialApi.constancia(telefono));
      if (!l.ok) {
        setError(l.code === "BAD_INPUT" ? "Escribí un teléfono válido." : textoError(l));
        return;
      }
      if (!l.data) { setError("El servidor no devolvió la constancia."); return; }
      setC(l.data);
    } catch (err) {
      setError(textoError(leerError(err)));
    } finally {
      setBuscando(false);
    }
  };

  const vacio = c && !c.consentimientos?.length && !c.bajas?.length && !c.mensajes?.length && !c.enviosConversaciones?.length && !c.base;

  return (
    <Tarjeta titulo="Buscar la constancia de un contacto"
      sub="Sirve para responder un reclamo: quién dio el consentimiento, cuándo y de dónde. La consulta queda registrada.">
      <form onSubmit={buscar} className="flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} inputMode="tel" autoComplete="off" placeholder="+5491155443322"
          aria-label="Teléfono del contacto"
          className="flex-1 min-w-[200px] rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500" />
        <button type="submit" disabled={!q.trim() || buscando}
          className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40">
          {buscando ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Buscar
        </button>
      </form>

      {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}

      {c && (
        <div className="mt-5 space-y-5">
          <p className="text-sm text-slate-600">Contacto: <span className="font-mono font-semibold text-slate-800">{destinoParaMostrar(c.destino, false)}</span></p>

          {vacio ? (
            // Busca en los archivos de clientes, la Base de datos, las bajas y lo que se le mandó: decía «en ninguna base»
            <p className="text-sm text-slate-500">{TEXTO_CONSTANCIA_VACIA}</p>
          ) : (
            <>
              <Bloque titulo="Consentimientos">
                {!c.consentimientos?.length ? <Vacio /> : (
                  <ul className="divide-y divide-slate-100">
                    {c.consentimientos.map((x, i) => (
                      <li key={`${x.campanaActivaId}-${i}`} className="py-2 flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-slate-800">{x.nombre}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CLS_OPTIN(x.optIn)}`}>{TEXTO_OPTIN(x.optIn)}</span>
                        <span className="text-xs text-slate-500">
                          {FUENTE_OPTIN[x.optInFuente] ?? x.optInFuente} · {x.optInFecha ? fecha(x.optInFecha) : "sin fecha"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Bloque>

              <Bloque titulo="Bajas">
                {!c.bajas?.length ? <Vacio /> : (
                  <ul className="divide-y divide-slate-100">
                    {c.bajas.map((b, i) => (
                      <li key={i} className="py-2 flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-slate-800">{b.canal}</span>
                        <span className="text-xs text-slate-500">{b.motivo || "sin motivo"} · {fechaHora(b.createdAt)}</span>
                        {!b.activo && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">levantada</span>}
                        {b.simulado === true && <ChipSimuladoNoSalio />}
                      </li>
                    ))}
                  </ul>
                )}
              </Bloque>

              {c.base && <ConstanciaEnLaBase base={c.base} />}

              <Bloque titulo="Mensajes del WhatsApp oficial" recortado={(c.mensajes?.length ?? 0) >= TOPE_CONSTANCIA}>
                {!c.mensajes?.length ? <Vacio /> : (
                  <ul className="divide-y divide-slate-100">
                    {c.mensajes.map((m, i) => (
                      <li key={`${m.campanaId}-${i}`} className="py-2 flex items-center gap-2 flex-wrap text-sm">
                        {m.campanaId === ID_PRUEBA
                          ? <span className="text-slate-800">{m.campanaNombre || "Prueba"}</span>
                          : <Link to={`${R}/campanas/${encodeURIComponent(m.campanaId)}`} className="text-slate-800 hover:text-violet-700">{m.campanaNombre}</Link>}
                        <ChipEstadoMensaje estado={m.estado} />
                        <span className="text-xs text-slate-500">{fechaHora(m.aceptadoAt || undefined)}</span>
                        {m.baja && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-600">Pidió la baja</span>}
                        {m.simulado === true && <ChipSimuladoNoSalio />}
                      </li>
                    ))}
                  </ul>
                )}
              </Bloque>

              {/* Lo mandado a mano desde la bandeja: ante un reclamo por una promoción mandada desde Conversaciones, sin
                  esta lista la constancia daba a entender que no se le había mandado nada. */}
              <Bloque titulo="Mandado desde Conversaciones" recortado={(c.enviosConversaciones?.length ?? 0) >= TOPE_CONSTANCIA}>
                {!c.enviosConversaciones?.length ? <Vacio /> : (
                  <ul className="divide-y divide-slate-100">
                    {c.enviosConversaciones.map((e, i) => (
                      <li key={i} className="py-2 flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-slate-800">{e.tipo === "plantilla" ? `Plantilla «${e.plantillaNombre || "sin nombre"}»` : "Respuesta de texto"}</span>
                        {e.tipo === "plantilla" && e.categoria !== "SERVICIO" && (
                          <span className="text-xs text-slate-500">{CATEGORIA[e.categoria] ?? e.categoria}</span>
                        )}
                        <ChipEstadoMensaje estado={e.estado} />
                        <span className="text-xs text-slate-500">{fechaHora(e.aceptadoAt || undefined)} · {nombreDeAutorPara(e.autor, equipo) || "sin nombre"}</span>
                        {e.simulado && <ChipSimuladoNoSalio />}
                      </li>
                    ))}
                  </ul>
                )}
              </Bloque>
            </>
          )}
        </div>
      )}
    </Tarjeta>
  );
}

/** "YYYY-MM-DD" (un día, sin hora) leído a las 12 locales: a medianoche UTC caería el día anterior en Argentina. */
const dia = (d: string) => fecha(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : d);

/** «En la base de datos» (SPEC-BD §6.9): el consentimiento que tiene ese teléfono en la base, de qué
 *  cargas vino y las 10 últimas entradas del historial. Es la constancia del art. 5 para los contactos
 *  que llegaron por un Excel y no por una campaña de Activa. */
function ConstanciaEnLaBase({ base }: { base: NonNullable<ConstanciaContacto["base"]> }) {
  // `por` es el mail del token: a una persona de un cliente, alguien del equipo es «Equipo Deenex», sin su mail.
  const equipo = useEsEquipoDeenex();
  const fuente = (f: string) => FUENTE_BASE[f] ?? (f || "sin fuente");
  return (
    <Bloque titulo="En la base de datos">
      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CLS_OPTIN(base.optIn)}`}>{TEXTO_OPTIN(base.optIn)}</span>
          <span className="text-xs text-slate-500">
            {fuente(base.fuente)}{base.detalle ? ` (${base.detalle})` : ""} · {base.fecha ? dia(base.fecha) : "sin fecha"}
          </span>
          <Link to={`${R}/base/contactos/${encodeURIComponent(base.contactoId)}`} className="text-xs font-semibold text-violet-600 hover:underline">Ver la ficha</Link>
        </div>
        {!!base.cargas?.length && (
          <p className="text-xs text-slate-500">
            Vino de: {base.cargas.map((x) => `${x.nombre}${x.fecha ? ` (${fecha(x.fecha)})` : ""}`).join(", ")}
          </p>
        )}
        {!!base.historial?.length && (
          <ul className="divide-y divide-slate-100">
            {base.historial.map((h, i) => (
              <li key={`${h.at}-${i}`} className="py-2 flex items-center gap-2 flex-wrap text-sm">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CLS_OPTIN(h.optIn)}`}>{TEXTO_OPTIN(h.optIn)}</span>
                <span className="text-xs text-slate-500">
                  {fuente(h.fuente)}{h.carga ? ` · ${h.carga.nombre}` : ""}{h.motivo ? ` · ${h.motivo}` : ""} · {h.fecha ? dia(h.fecha) : "sin fecha"}
                  {h.por ? ` · ${equipo ? h.por : nombreDeQuienParaCliente(h.por)}` : ""} · registrado {fechaHora(h.at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Bloque>
  );
}

/**
 * `recortado`: el backend manda los 20 más recientes (`constanciaContacto`, `TOPE_CONSTANCIA`). La constancia promete
 * lo que se le mandó a la persona, para contestar un reclamo, y cortaba en 20 sin decirlo: con 20, lo dice.
 */
function Bloque({ titulo, recortado = false, children }: { titulo: string; recortado?: boolean; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">{titulo}</h3>
      {recortado && <p className="text-xs text-slate-500 mb-1">{TEXTO_CONSTANCIA_RECORTADA}</p>}
      {children}
    </div>
  );
}

function Vacio() {
  return <p className="text-sm text-slate-400">Nada registrado.</p>;
}

function TablaConsentimientos() {
  const [lista, setLista] = useState<Consentimiento[] | null>(null);
  const [error, setError] = useState("");
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    let vivo = true;
    setError("");
    (async () => {
      try {
        const l = leerRespuesta<Consentimiento[]>(await oficialApi.consentimientos());
        if (!vivo) return;
        if (!l.ok) setError(textoError(l));
        else if (!Array.isArray(l.data)) setError("El servidor no devolvió los consentimientos.");
        else setLista(l.data);
      } catch (e) {
        if (vivo) setError(textoError(leerError(e)));
      }
    })();
    return () => { vivo = false; };
  }, [intento]);

  const fuentes = (p: Consentimiento["porFuente"]) =>
    Object.entries(p || {})
      .filter(([, n]) => Number(n) > 0)
      .map(([f, n]) => `${FUENTE_OPTIN[f as FuenteOptIn] ?? f} (${num(Number(n))})`)
      .join(" · ") || "—";

  return (
    <Tarjeta titulo="Consentimientos"
      sub='Se cuentan los clientes con celular: por WhatsApp no se le puede escribir a nadie más. En WhatsApp oficial sólo se escribe a quien dijo que sí, sin importar la política de consentimiento de las campañas por QR. Los "sin dato" son una tarea: hay que preguntarles.'>
      {error ? (
        <div>
          <p className="text-sm text-red-600">No pudimos traer los consentimientos. {error}</p>
          <button type="button" onClick={() => setIntento((n) => n + 1)} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
            <RefreshCw size={14} /> Reintentar
          </button>
        </div>
      ) : !lista ? (
        <div className="h-24 rounded-xl bg-slate-100 animate-pulse" aria-busy="true" />
      ) : lista.length === 0 ? (
        <p className="text-sm text-slate-400">Todavía no se analizó ningún archivo de clientes.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                {/* «Base» era jerga: cada fila es un archivo de clientes analizado en Reactivación */}
                <th className="pb-2 pr-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Archivo de clientes</th>
                <th className="pb-2 pr-4 text-[10px] font-bold uppercase tracking-wider text-slate-400 text-right">Con celular</th>
                <th className="pb-2 pr-4 text-[10px] font-bold uppercase tracking-wider text-slate-400 text-right">Dijeron que sí</th>
                <th className="pb-2 pr-4 text-[10px] font-bold uppercase tracking-wider text-slate-400 text-right">Sin dato</th>
                <th className="pb-2 pr-4 text-[10px] font-bold uppercase tracking-wider text-slate-400 text-right">Dijeron que no</th>
                <th className="pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">De dónde</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lista.map((c) => {
                // los clientes del análisis, con y sin celular (un backend anterior no los manda: la suma de los tres)
                const clientes = typeof c.clientes === "number" ? c.clientes : (Number(c.optInTrue) || 0) + (Number(c.optInNull) || 0) + (Number(c.optInFalse) || 0);
                // Sin `conCelular` (un backend anterior) no hay cifras con celular: «—», nunca las de todos bajo este título.
                const k = c.conCelular;
                return (
                  <tr key={c.campanaActivaId}>
                    {/* Todos los del mes se llaman igual («Reactivación de octubre de 2026»): la hora, los clientes y el
                        comercio dicen cuál es cuál (`etiquetaAnalisis`, la misma del selector de la Audiencia). */}
                    <td className="py-2 pr-4 text-slate-800">
                      {etiquetaAnalisis({ nombre: c.nombre, marca: c.marca, clientes, createdAt: c.createdAt })}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums text-slate-800 whitespace-nowrap">
                      {k ? <>{num(k.total)}<span className="text-xs text-slate-400"> de {num(clientes)}</span></> : "—"}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums font-semibold text-emerald-700">{k ? num(k.optInTrue) : "—"}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-slate-600">{k ? num(k.optInNull) : "—"}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-slate-600">{k ? num(k.optInFalse) : "—"}</td>
                    <td className="py-2 text-xs text-slate-500">{k ? fuentes(k.porFuente) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Tarjeta>
  );
}
